import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatCurrency } from "../lib/formatCurrency";
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, BookOpen, FileText, TrendingUp, TrendingDown, Scale, ArrowRight, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'];

const typeColors = {
  asset: 'bg-blue-100 text-blue-700',
  liability: 'bg-rose-100 text-rose-700',
  equity: 'bg-purple-100 text-purple-700',
  revenue: 'bg-emerald-100 text-emerald-700',
  expense: 'bg-amber-100 text-amber-700'
};


// Account Form Component
const AccountForm = ({ account, onSave, onClose }) => {
  const [form, setForm] = useState(account || {
    code: '', name: '', type: 'asset', parent_code: '', description: '', is_active: true
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (account?.id) {
        await axios.put(`${API}/accounts/${account.id}`, form);
        toast.success('Account updated');
      } else {
        await axios.post(`${API}/accounts`, form);
        toast.success('Account created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Account Code *</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required placeholder="e.g., 1110" data-testid="account-code-input" />
        </div>
        <div className="space-y-2">
          <Label>Account Type *</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Account Name *</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g., Cash and Cash Equivalents" />
      </div>
      <div className="space-y-2">
        <Label>Parent Account Code</Label>
        <Input value={form.parent_code} onChange={(e) => setForm({ ...form, parent_code: e.target.value })} placeholder="e.g., 1100" />
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-account-btn">{saving ? 'Saving...' : 'Save Account'}</Button>
      </div>
    </form>
  );
};

// Journal Entry Form Component
const JournalEntryForm = ({ accounts, onSave, onClose }) => {
  const [form, setForm] = useState({
    reference: '', description: '', date: new Date().toISOString().split('T')[0],
    lines: [
      { account_code: '', debit: 0, credit: 0, description: '' },
      { account_code: '', debit: 0, credit: 0, description: '' }
    ]
  });
  const [saving, setSaving] = useState(false);

  const totalDebit = form.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  const totalCredit = form.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const updateLine = (index, field, value) => {
    const newLines = [...form.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setForm({ ...form, lines: newLines });
  };

  const addLine = () => {
    setForm({ ...form, lines: [...form.lines, { account_code: '', debit: 0, credit: 0, description: '' }] });
  };

  const removeLine = (index) => {
    if (form.lines.length > 2) {
      const newLines = form.lines.filter((_, i) => i !== index);
      setForm({ ...form, lines: newLines });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isBalanced) {
      toast.error('Journal entry must balance (Debits = Credits)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/journal-entries`, form);
      toast.success('Journal entry posted');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post journal entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Reference *</Label>
          <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} required placeholder="e.g., INV-001" data-testid="je-reference-input" />
        </div>
        <div className="space-y-2">
          <Label>Date *</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description *</Label>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="e.g., Record sales invoice" />
      </div>

      <div className="space-y-2">
        <Label>Journal Lines</Label>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead className="w-32 text-right">Debit</TableHead>
                <TableHead className="w-32 text-right">Credit</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.lines.map((line, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Select value={line.account_code} onValueChange={(v) => updateLine(idx, 'account_code', v)}>
                      <SelectTrigger className="w-full"><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent>
                        {accounts.map(acc => (
                          <SelectItem key={acc.code} value={acc.code}>
                            {acc.code} - {acc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input type="number" min="0" step="0.01" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)} className="text-right font-mono" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min="0" step="0.01" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)} className="text-right font-mono" />
                  </TableCell>
                  <TableCell>
                    <Input value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} placeholder="Line description" />
                  </TableCell>
                  <TableCell>
                    {form.lines.length > 2 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(idx)}>×</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addLine}>+ Add Line</Button>
      </div>

      <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total Debit</p>
          <p className="text-lg font-mono font-semibold">{formatCurrency(totalDebit)}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total Credit</p>
          <p className="text-lg font-mono font-semibold">{formatCurrency(totalCredit)}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Difference</p>
          <p className={`text-lg font-mono font-semibold ${isBalanced ? 'text-emerald-600' : 'text-rose-600'}`}>
            {formatCurrency(Math.abs(totalDebit - totalCredit))}
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving || !isBalanced} data-testid="post-je-btn">
          {saving ? 'Posting...' : 'Post Journal Entry'}
        </Button>
      </div>
    </form>
  );
};

// Main Accounting Page Component
export const AccountingPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accRes, jeRes] = await Promise.all([
        axios.get(`${API}/accounts`),
        axios.get(`${API}/journal-entries`)
      ]);
      setAccounts(accRes.data);
      setJournalEntries(jeRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrialBalance = async () => {
    try {
      const res = await axios.get(`${API}/reports/trial-balance`);
      setTrialBalance(res.data);
    } catch (error) {
      toast.error('Failed to load trial balance');
    }
  };

  const loadIncomeStatement = async () => {
    try {
      const res = await axios.get(`${API}/reports/income-statement`);
      setIncomeStatement(res.data);
    } catch (error) {
      toast.error('Failed to load income statement');
    }
  };

  const loadBalanceSheet = async () => {
    try {
      const res = await axios.get(`${API}/reports/balance-sheet`);
      setBalanceSheet(res.data);
    } catch (error) {
      toast.error('Failed to load balance sheet');
    }
  };

  const seedDefaultAccounts = async () => {
    try {
      const res = await axios.post(`${API}/accounts/seed-default`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Failed to seed accounts');
    }
  };

  const deleteAccount = async (id) => {
  if (!window.confirm("Delete this account?")) return;

  try {
    await axios.delete(`${API}/accounts/${id}`);
    toast.success("Account deleted");
    loadData();
  } catch (error) {
    toast.error(error.response?.data?.detail || "Failed to delete account");
  }
};

  const filteredAccounts = accounts.filter(a =>
    a.code.includes(search) || a.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Accounting</h1>
          <p className="text-muted-foreground mt-1">Chart of Accounts, General Ledger & Financial Reports</p>
        </div>
      </div>

      <Tabs defaultValue="accounts" className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="accounts" data-testid="accounts-tab">Chart of Accounts ({accounts.length})</TabsTrigger>
            <TabsTrigger value="journal" data-testid="journal-tab">Journal Entries ({journalEntries.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab">Financial Reports</TabsTrigger>
          </TabsList>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." className="pl-9 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Chart of Accounts Tab */}
        <TabsContent value="accounts" className="space-y-4">
          <div className="flex justify-end gap-2">
            {accounts.length === 0 && (
              <Button variant="outline" onClick={seedDefaultAccounts} data-testid="seed-accounts-btn">
                Seed Default Accounts
              </Button>
            )}
            <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedAccount(null)} data-testid="add-account-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Account
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>{selectedAccount ? 'Edit Account' : 'New Account'}</DialogTitle>
                </DialogHeader>
                <AccountForm account={selectedAccount} onSave={loadData} onClose={() => setAccountDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedAccount(account); setAccountDialogOpen(true); }}>
                    <TableCell className="font-mono text-sm">{account.code}</TableCell>
                    <TableCell className="font-medium">{account.name}</TableCell>
                    <TableCell><Badge className={typeColors[account.type]}>{account.type}</Badge></TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{account.parent_code || '-'}</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${account.balance >= 0 ? '' : 'text-rose-600'}`}>
                      {formatCurrency(account.balance)}
                    </TableCell>
                    <TableCell className="text-right">
  <Button
    variant="destructive"
    size="sm"
    onClick={(e) => {
      e.stopPropagation();
      deleteAccount(account.id);
    }}
  >
    Delete
  </Button>
</TableCell>
                  </TableRow>
                ))}
                {filteredAccounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No accounts found. Click "Seed Default Accounts" to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Journal Entries Tab */}
        <TabsContent value="journal" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={journalDialogOpen} onOpenChange={setJournalDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-journal-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Journal Entry
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle>Create Journal Entry</DialogTitle>
                </DialogHeader>
                <JournalEntryForm accounts={accounts} onSave={loadData} onClose={() => setJournalDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entry #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {journalEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono text-sm">{entry.entry_number}</TableCell>
                    <TableCell>{entry.date}</TableCell>
                    <TableCell className="font-medium">{entry.reference}</TableCell>
                    <TableCell className="text-muted-foreground">{entry.description}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(entry.total_debit)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(entry.total_credit)}</TableCell>
                    <TableCell><Badge className="bg-emerald-100 text-emerald-700">{entry.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {journalEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No journal entries found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Financial Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={loadTrialBalance}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Scale className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Trial Balance</p>
                    <p className="text-sm text-muted-foreground">Click to generate</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={loadIncomeStatement}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Income Statement</p>
                    <p className="text-sm text-muted-foreground">Profit & Loss</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={loadBalanceSheet}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold">Balance Sheet</p>
                    <p className="text-sm text-muted-foreground">Financial Position</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trial Balance Report */}
          {trialBalance && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Trial Balance</span>
                  <Badge className={trialBalance.balanced ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
                    {trialBalance.balanced ? 'Balanced' : 'Not Balanced'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trialBalance.accounts.map((acc, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono">{acc.code}</TableCell>
                        <TableCell>{acc.name}</TableCell>
                        <TableCell><Badge className={typeColors[acc.type]}>{acc.type}</Badge></TableCell>
                        <TableCell className="text-right font-mono">{acc.debit > 0 ? formatCurrency(acc.debit) : '-'}</TableCell>
                        <TableCell className="text-right font-mono">{acc.credit > 0 ? formatCurrency(acc.credit) : '-'}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted font-semibold">
                      <TableCell colSpan={3}>TOTALS</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(trialBalance.total_debit)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(trialBalance.total_credit)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Income Statement Report */}
          {incomeStatement && (
            <Card>
              <CardHeader>
                <CardTitle>Income Statement (Profit & Loss)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-emerald-600 mb-2">Revenue</h3>
                  <Table>
                    <TableBody>
                      {incomeStatement.revenue.accounts.map((acc, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{acc.code} - {acc.name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(acc.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-emerald-50 font-semibold">
                        <TableCell>Total Revenue</TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(incomeStatement.revenue.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div>
                  <h3 className="font-semibold text-rose-600 mb-2">Expenses</h3>
                  <Table>
                    <TableBody>
                      {incomeStatement.expenses.accounts.map((acc, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{acc.code} - {acc.name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(acc.amount)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-rose-50 font-semibold">
                        <TableCell>Total Expenses</TableCell>
                        <TableCell className="text-right font-mono text-rose-600">{formatCurrency(incomeStatement.expenses.total)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="p-4 rounded-lg bg-slate-900 text-white">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Net Income</span>
                    <span className={`text-2xl font-mono font-bold ${incomeStatement.net_income >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatCurrency(incomeStatement.net_income)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

              {/* Balance Sheet Report */}
{balanceSheet && (
  <Card>
    <CardHeader>
      <CardTitle>Balance Sheet</CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">

      {/* Assets */}
      <div>
        <h3 className="font-semibold text-blue-600 mb-2">Assets</h3>
        <Table>
          <TableBody>
            {balanceSheet.assets.accounts.map((acc, idx) => (
              <TableRow key={idx}>
                <TableCell>{acc.code} - {acc.name}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatCurrency(acc.amount)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-blue-50 font-semibold">
              <TableCell>Total Assets</TableCell>
              <TableCell className="text-right font-mono text-blue-600">
                {formatCurrency(balanceSheet.assets.total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Liabilities */}
      <div>
        <h3 className="font-semibold text-rose-600 mb-2">Liabilities</h3>
        <Table>
          <TableBody>
            {balanceSheet.liabilities.accounts.map((acc, idx) => (
              <TableRow key={idx}>
                <TableCell>{acc.code} - {acc.name}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatCurrency(acc.amount)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-rose-50 font-semibold">
              <TableCell>Total Liabilities</TableCell>
              <TableCell className="text-right font-mono text-rose-600">
                {formatCurrency(balanceSheet.liabilities.total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Equity */}
      <div>
        <h3 className="font-semibold text-purple-600 mb-2">Equity</h3>
        <Table>
          <TableBody>
            {balanceSheet.equity.accounts.map((acc, idx) => (
              <TableRow key={idx}>
                <TableCell>{acc.code} - {acc.name}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatCurrency(acc.amount)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-purple-50 font-semibold">
              <TableCell>Total Equity</TableCell>
              <TableCell className="text-right font-mono text-purple-600">
                {formatCurrency(balanceSheet.equity.total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

    </CardContent>
  </Card>
)}
        </TabsContent>
      </Tabs>
    </div>
    
  );
};
